import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { normalizeAddress } from './addressUtils';

/**
 * Get organization verification data for a specific address
 *
 * Uses chain-agnostic address normalization
 *
 * @param {string} address - The address to get verification data for
 * @param {string} chain - The blockchain chain (defaults to 'ethereum', kept for compatibility)
 * @returns {Promise<Object|null>} The verification data or null if not found
 */
export const getOrgVerification = async (address, chain = 'ethereum') => {
  if (!address) return null;

  try {
    // Normalize address (chain-agnostic)
    // EVM: lowercase hex (0xabc...)
    // Non-EVM: preserve case (1ABC...)
    const uid = normalizeAddress(address);
    const addressRef = doc(db, 'addresses', uid);
    const addressDoc = await getDoc(addressRef);

    if (addressDoc.exists()) {
      const data = addressDoc.data();

      // Check both org info and entity info
      const hasOrgInfo = data.orgName || data.logoUrl;
      const hasEntityInfo = data.entity || data.entityLogo;

      // If no info from either source, return null
      if (!hasOrgInfo && !hasEntityInfo) {
        return null;
      }

      // Priority: org info > entity info
      if (hasOrgInfo) {
        // Return org verification data
        return {
          orgName: data.orgName || null,
          logoUrl: data.logoUrl || null,
          homepage: data.homepage || null,
          claimedAt: data.onboardedAt || null,  // After migration, orgAddrClaimedTs was renamed to onboardedAt
          verified: true
        };
      } else {
        // Return entity verification data (from Moralis labels)
        return {
          orgName: data.entity || null,
          logoUrl: data.entityLogo || null,
          homepage: null,  // Entity data doesn't have homepage
          claimedAt: null,  // Entity data doesn't have claim timestamp
          verified: true
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting organization verification:', error);
    return null;
  }
};

/**
 * Get organization verification data for multiple addresses
 * @param {Array<string>} addresses - Array of addresses to get verification data for
 * @param {string} chain - The blockchain chain (defaults to 'ethereum')
 * @returns {Promise<Object>} Map of address -> verification data
 */
export const getBatchOrgVerifications = async (addresses, chain = 'ethereum') => {
  if (!addresses || addresses.length === 0) return {};

  try {
    const verifications = {};

    // Fetch all verifications in parallel
    await Promise.all(
      addresses.map(async (address) => {
        const verification = await getOrgVerification(address, chain);
        if (verification) {
          verifications[address.toLowerCase()] = verification;
        }
      })
    );

    return verifications;
  } catch (error) {
    console.error('Error getting batch organization verifications:', error);
    return {};
  }
};
